/**
 * RevenueDistributionMethod value object — shared domain primitive.
 *
 * Originally introduced for the `Organization` aggregate (SCRUM-88 / DS-008-5),
 * this concept is consumed across THREE bounded contexts:
 *   - `identity` — the `Organization` aggregate persists the org-level default
 *     (`revenue_distribution_method`) and validates it on write.
 *   - `taxes-and-fees` — `Tax` / `Fee` aggregates carry a per-entity override.
 *   - `booking` — the reservation charges calculator resolves the 3-tier
 *     distribution method per line item and tier-gates PRO methods on Lite.
 *
 * Because it is a stable, dependency-free enum/VO shared by multiple BCs, it
 * lives in `@artemis/domain` — the canonical home for cross-BC domain
 * primitives — mirroring `organization-status.ts` and `platform-role.ts`. A
 * downstream BC's domain depends on this shared package (an allowed dependency
 * per the hexagonal layer rule), NOT on a sibling BC's domain. See
 * ADR 0027 (shared-kernel for cross-BC value objects).
 *
 * Enum source of truth: spec §3.1 + DS-012 Taxes & Fees BC §7.1 — the Jira
 * enum strings are STALE. The authoritative literals are exactly:
 *   - `'check_in'`
 *   - `'check_out'`
 *   - `'nightly'`
 *   - `'payments_receivable'`
 *   - `'upon_receipt'`        (SCRUM-211 — Lite-tier naive-cash distribution)
 *
 * The DB CHECK constraint (re-named + expanded by the SCRUM-211 migration)
 * mirrors these five literals byte-identically, as do the Zod schema and the
 * two `@ApiProperty` enum decorators. Drift = silent VO accept + DB reject →
 * 500 instead of 422. The cross-site byte-equality is enforced by
 * `revenue-distribution-method.drift.spec.ts` (in the identity interface
 * layer), with `REVENUE_DISTRIBUTION_METHOD_VALUES` (below) as the single
 * source of truth every other site is asserted against.
 *
 * Tier note (SCRUM-211): `nightly` / `payments_receivable` are PRO-only;
 * `check_in` / `check_out` / `upon_receipt` are Lite. The VO is tier-agnostic
 * — it accepts every literal; the PRO/Lite reject lives in the application
 * layer (`UpdateOrgOwnerSettingsUseCase` via `OrganizationTierPort`).
 *
 * VO contract:
 *   - `static create(raw: unknown): RevenueDistributionMethod` — strict
 *     validator; throws `InvalidRevenueDistributionMethodError` on unknown.
 *   - `.value` getter returns the literal.
 *   - `.equals(other: RevenueDistributionMethod | unknown): boolean` — deep
 *     equality. Returns `false` for non-VO arguments.
 *
 * Boundary discipline (AUDIT-216 / CLAUDE.md):
 *   No imports — pure TypeScript primitive enum class.
 */
/**
 * Canonical, ordered source-of-truth value list. Every other enumeration site
 * (Zod schema, both `@ApiProperty` enums, `openapi.json`, `openapi.d.ts`, the
 * DB CHECK) is asserted byte-equal against this array by
 * `revenue-distribution-method.drift.spec.ts`. Frozen so it cannot be mutated
 * at runtime; the literal union below is derived from it.
 */
export const REVENUE_DISTRIBUTION_METHOD_VALUES = [
    'check_in',
    'check_out',
    'nightly',
    'payments_receivable',
    'upon_receipt',
];
const VALID_VALUES = new Set(REVENUE_DISTRIBUTION_METHOD_VALUES);
/**
 * Revenue-distribution methods that require the PRO subscription tier
 * (DS-012 Taxes & Fees BC §7.4). `nightly` and `payments_receivable` are
 * PRO-only; `check_in` / `check_out` / `upon_receipt` are available on Lite.
 *
 * This is domain knowledge (the classification), kept here as the single
 * source. The tier *enforcement* (resolve the org's tier, reject on mismatch)
 * is an application-layer concern — see `UpdateOrgOwnerSettingsUseCase`.
 */
const PRO_TIER_VALUES = new Set(['nightly', 'payments_receivable']);
/** True iff the value requires the PRO tier (DS-012 §7.4). */
export function isProTierRevenueDistributionMethod(value) {
    return PRO_TIER_VALUES.has(value);
}
export class InvalidRevenueDistributionMethodError extends Error {
    constructor(raw) {
        super(`Invalid RevenueDistributionMethod: expected one of ${REVENUE_DISTRIBUTION_METHOD_VALUES.map((v) => `'${v}'`).join(' | ')}; received ${safeJsonStringify(raw)}`);
        this.name = 'InvalidRevenueDistributionMethodError';
    }
}
function safeJsonStringify(value) {
    try {
        return JSON.stringify(value);
    }
    catch {
        return String(value);
    }
}
export class RevenueDistributionMethod {
    value;
    constructor(value) {
        this.value = value;
    }
    /**
     * Strict factory. Accepts only the five literal strings; everything else
     * (empty, whitespace, wrong type, case-mismatch, stale Jira values such as
     * `'per_reservation'`, injection-shaped, trailing-separator) throws
     * `InvalidRevenueDistributionMethodError`.
     */
    static create(raw) {
        if (typeof raw !== 'string') {
            throw new InvalidRevenueDistributionMethodError(raw);
        }
        if (!VALID_VALUES.has(raw)) {
            throw new InvalidRevenueDistributionMethodError(raw);
        }
        return new RevenueDistributionMethod(raw);
    }
    /**
     * Deep equality — two `RevenueDistributionMethod` instances are equal iff
     * they hold the same literal. The signature is typed strictly to the VO so
     * non-VO arguments fail TypeScript at the boundary; the runtime
     * `instanceof` is defense-in-depth for erased paths.
     */
    equals(other) {
        if (!(other instanceof RevenueDistributionMethod))
            return false;
        return this.value === other.value;
    }
}
//# sourceMappingURL=revenue-distribution-method.js.map